import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, MarkerF, InfoWindowF, DirectionsRenderer } from '@react-google-maps/api';
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import "../../../Styles/Styles.css"
import "bootstrap/dist/css/bootstrap.min.css";
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useGoogleMaps } from '../../../contexts/GoogleMapsContext';
import {
  fetchUnviableOptions,
  addUnviableOption,
  updateUnviableOption,
  removeUnviableOption
} from '../../../Services/FirebaseUtils';

const containerStyle = {
  width: '100%',
  height: '50vh',
  border: "2px solid #ddd"
};

// Custom map styles
const mapStyles = [
  {
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#f5f5f5"
      }
    ]
  },
  {
    "elementType": "labels.icon",
    "stylers": [
      {
        "visibility": "off"
      }
    ]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#616161"
      }
    ]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "color": "#f5f5f5"
      }
    ]
  },
  {
    "featureType": "administrative.land_parcel",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#bdbdbd"
      }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#eeeeee"
      }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#e5e5e5"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#9e9e9e"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#ffffff"
      }
    ]
  },
  {
    "featureType": "road.arterial",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#dadada"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#616161"
      }
    ]
  },
  {
    "featureType": "road.local",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#9e9e9e"
      }
    ]
  },
  {
    "featureType": "transit.line",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#e5e5e5"
      }
    ]
  },
  {
    "featureType": "transit.station",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#eeeeee"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#ADD8E6"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#9e9e9e"
      }
    ]
  }
];

const mapOptions = {
  styles: mapStyles,
  disableDefaultUI: true,
  fullscreenControl: true,
  zoomControl: true,
};

const formatPrice = (price) =>
  price !== undefined && price !== null
    ? new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price)
    : "N/A";

const Recommandedlocation = ({
  proposal,
  selectedTab,
  onTabChange,
  isEditable = false
}) => {
  const { isLoaded } = useGoogleMaps();
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoom, setZoom] = useState(13);
  const [center, setCenter] = useState({ lat: 13.0827, lng: 80.2707 });
  const [searchedPlace, setSearchedPlace] = useState(null);
  const [directions, setDirections] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [unviableOptions, setUnviableOptions] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Fetch existing unviable options when component mounts or proposal changes
  useEffect(() => {
    const fetchUnviableOptionsAsync = async () => {
      try {
        if (proposal?.id) {
          const result = await fetchUnviableOptions(proposal.id);
          setUnviableOptions(result.success ? result.unviableOptions || [] : []);
        } else if (proposal?.unviableOptions && proposal.unviableOptions.length > 0) {
          // For public view, use unviable options directly from the proposal object
          setUnviableOptions(proposal.unviableOptions);
        } else {
          setUnviableOptions([]);
        }
      } catch (error) {
        console.error('Error fetching unviable options:', error);
        setUnviableOptions([]);
      }
    };

    // Fetch unviable options regardless of edit mode
    fetchUnviableOptionsAsync();
  }, [proposal?.id, proposal?.unviableOptions]);

  // Update an unviable option
  const updateUnviableOptionAsync = (optionId, field, value) => {
    setUnviableOptions(prev =>
      prev.map(option =>
        option.id === optionId
          ? { ...option, [field]: value, requiresSave: true }
          : option
      )
    );
  };

  // Remove an unviable option
  const removeUnviableOptionAsync = async (optionId) => {
    // If it's a temporary option, just remove from state
    if (optionId.startsWith('temp-')) {
      setUnviableOptions(prev => prev.filter(option => option.id !== optionId));
      return;
    }

    try {
      // Remove from Firestore
      const result = await removeUnviableOption(proposal.id, optionId);

      if (result.success) {
        // Remove from local state
        setUnviableOptions(prev => prev.filter(option => option.id !== optionId));

        // Show success notification
        setOpenSnackbar(true);
        setSnackbarMessage('Unviable option removed successfully');
      } else {
        // Show error notification
        setOpenSnackbar(true);
        setSnackbarMessage('Failed to remove unviable option');
      }
    } catch (error) {
      console.error('Error removing unviable option:', error);
      setOpenSnackbar(true);
      setSnackbarMessage('An error occurred while removing the option');
    }
  };

  // Add new unviable option row
  const addUnviableOptionAsync = () => {
    if (!isEditable || !proposal?.id) return;

    // Generate a temporary unique ID
    const tempId = `temp-${Date.now()}`;

    // Create a new option with default empty values
    const newOption = {
      id: tempId,
      spaceName: '',
      unviableReason: '',
      isNew: true
    };

    // Add the new option to the state
    setUnviableOptions(prev => [...prev, newOption]);

    // Automatically enter edit mode for the new row
    setEditingOptionId(tempId);
  };

  // Save unviable options
  const saveUnviableOptionsAsync = async () => {
    if (!isEditable || !proposal?.id) return;

    try {
      // Get the option being edited
      const editedOption = unviableOptions.find(option => option.id === editingOptionId);
      
      if (editedOption) {
        // If editing an existing option, update it in Firestore
        if (!editedOption.id.startsWith('temp-')) {
          const result = await updateUnviableOption(proposal.id, editedOption.id, {
            spaceName: editedOption.spaceName || '',
            unviableReason: editedOption.unviableReason || ''
          });
          
          if (!result.success) {
            throw new Error(result.error || 'Failed to update option');
          }
        } 
        // If editing a temporary option, add it to Firestore
        else {
          const result = await addUnviableOption(proposal.id, {
            spaceName: editedOption.spaceName || '',
            unviableReason: editedOption.unviableReason || ''
          });
          
          if (!result.success) {
            throw new Error(result.error || 'Failed to add option');
          }
          
          // Remove the temporary option from state
          setUnviableOptions(prev => prev.filter(opt => opt.id !== editedOption.id));
        }
      }

      // Fetch fresh data after saving
      const fetchResult = await fetchUnviableOptions(proposal.id);

      if (fetchResult.success) {
        // Update state with fetched options
        setUnviableOptions(fetchResult.unviableOptions || []);
        
        // Reset editing state
        setEditingOptionId(null);
        
        // Show success notification
        setOpenSnackbar(true);
        setSnackbarMessage('Unviable options saved successfully');
      } else {
        throw new Error(fetchResult.error || 'Failed to fetch updated options');
      }
    } catch (error) {
      console.error('Error saving unviable options:', error);
      setOpenSnackbar(true);
      setSnackbarMessage(`Error: ${error.message || 'An error occurred while saving'}`);
    }
  };

  // Fetch unviable options when in editable mode
  useEffect(() => {
    const fetchUnviableOptionsAsync = async () => {
      try {
        if (proposal?.id && isEditable) {
          const result = await fetchUnviableOptions(proposal.id);
          if (result.success) {
            setUnviableOptions(result.unviableOptions || []);
          } else {
            console.error('Failed to fetch unviable options');
            setUnviableOptions([]);
          }
        }
      } catch (error) {
        console.error('Error fetching unviable options:', error);
        setUnviableOptions([]);
      }
    };

    fetchUnviableOptionsAsync();
  }, [proposal?.id, isEditable]);

  // Handle edit mode toggle
  const toggleEditMode = (optionId) => {
    setEditingOptionId(optionId === editingOptionId ? null : optionId);
  };

  // Filter options based on selected tab
  const recommendedOptions = proposal?.proposedOptions?.filter(option => option.isRecommended) || [];
  const otherOptions = proposal?.proposedOptions?.filter(option => !option.isRecommended && !option.isNonViable) || [];
  const currentOptions =
    selectedTab === 0 ? recommendedOptions :
      selectedTab === 1 ? otherOptions :
        selectedTab === 2 ? unviableOptions : [];

  // Create DirectionsService instance
  const directionsService = isLoaded ? new window.google.maps.DirectionsService() : null;

  // Function to calculate and display directions
  const calculateDirections = useCallback((destination) => {
    if (!searchedPlace || !directionsService) return;
    // Clear previous directions
    setDirections(null);
    const request = {
      origin: searchedPlace.position,
      destination: { lat: Number(destination.lat), lng: Number(destination.lng) },
      travelMode: window.google.maps.TravelMode.DRIVING
    };

    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);
        setSelectedDestination(destination);
      }
    });
  }, [searchedPlace, directionsService]);

  // Clear directions when search place changes
  useEffect(() => {
    setDirections(null);
    setSelectedDestination(null);
  }, [searchedPlace]);

  // Get locations for the current tab
  const locations = currentOptions
    .filter(option => option.location && option.location.lat && option.location.lng)
    .map((option) => {
      const loc = option.location;
      return {
        lat: Number(loc.lat),
        lng: Number(loc.lng),
        centerName: option.centerName,
        locality: option.locality,
        billableSeats: option.billableSeats,
        pricePerSeat: formatPrice(option.pricePerSeat),
        availableFrom: option.availableFrom,
        address: loc.address
      };
    });

  useEffect(() => {
    if (isLoaded) {
      const input = document.getElementById('location-search');
      const options = {
        componentRestrictions: { country: 'in' },
        types: ['geocode', 'establishment'],
        fields: ['address_components', 'geometry', 'name', 'formatted_address']
      };
      const autocomplete = new window.google.maps.places.Autocomplete(input, options);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const newLocation = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          setCenter(newLocation);
          setSearchedPlace({
            position: newLocation,
            name: place.name,
            address: place.formatted_address
          });
          if (map) {
            map.panTo(newLocation);
            map.setZoom(15);
          }
        }
      });
    }
  }, [isLoaded, map]);

  useEffect(() => {
    if (locations.length > 0 && isLoaded && map) {
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach((location) => {
        bounds.extend(new window.google.maps.LatLng(Number(location.lat), Number(location.lng)));
      });
      map.fitBounds(bounds);

      if (searchedPlace) {
        bounds.extend(new window.google.maps.LatLng(searchedPlace.position.lat, searchedPlace.position.lng));
        map.fitBounds(bounds);
      }
    }

  }, [selectedTab, locations, map, center.lat, center.lng, searchedPlace]);

  const handleTabChange = async (event, newValue) => {
    onTabChange(newValue);
    setSelectedMarker(null);

    // Update Firestore
    try {
      if (proposal?.id) {
        const proposalRef = doc(db, 'proposals', proposal.id);
        await updateDoc(proposalRef, {
          proposedOptions: proposal.proposedOptions
        });
      }
    } catch (error) {
      console.error('Error updating Firestore:', error);
    }
  };

  const onLoad = useCallback((map) => {
    if (locations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach((location) => {
        bounds.extend(new window.google.maps.LatLng(Number(location.lat), Number(location.lng)));
      });
      map.fitBounds(bounds);
    }
    setMap(map);
  }, [locations]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleTouchStart = (event, rowId) => {
    if (map && locations[rowId]) {
      map.panTo({
        lat: Number(locations[rowId].lat),
        lng: Number(locations[rowId].lng)
      });
      setSelectedMarker(locations[rowId]);
    }
  };

  const handleMarkerClick = (location) => {
    setSelectedMarker(location);
    if (searchedPlace) {
      calculateDirections(location);
    }
  };

  return isLoaded ? (
    <div className="container mt-4">
      <h1 className="display-4 text-start mb-4">
        Proposed Options
      </h1>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab sx={{ fontWeight: 'bold', fontSize: '1rem' }} label={`Recommended Options (${recommendedOptions.length})`} />
          <Tab sx={{ fontWeight: 'bold', fontSize: '1rem' }} label={`Other Options (${otherOptions.length})`} />
          <Tab sx={{ fontWeight: 'bold', fontSize: '1rem' }} label={`Non Viable Options (${unviableOptions.length})`} />
        </Tabs>
      </Box>

      {/* Render map and location table only for first two tabs */}
      {selectedTab !== 2 && (
        <div className="row">
          <div className="col-lg-6 col-md-12 mb-3">
            <input
              id="location-search"
              type="text"
              placeholder="Find distance from your location"
              style={{
                width: '100%',
                height: '40px',
                padding: '0 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          </div>
        </div>
      )}

      {selectedTab !== 2 && (
        <div className="row">
          <div className="col-lg-6 col-md-12">
            <div style={{ width: '100%', height: '50vh', border: '2px solid #ddd' }}>
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={zoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={mapOptions}
              >
                {/* Render directions if available */}
                {directions && (
                  <DirectionsRenderer
                    directions={directions}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: "#2196F3",
                        strokeWeight: 4
                      }
                    }}
                  />
                )}

                {/* Markers for locations */}
                {locations.map((location, index) => (
                  <MarkerF
                    key={index}
                    position={{ lat: Number(location.lat), lng: Number(location.lng) }}
                    onClick={() => handleMarkerClick(location)}
                    options={{ clickable: true }}
                    label={`${index + 1}`}
                  />
                ))}

                {/* Special marker for searched place */}
                {searchedPlace && (
                  <MarkerF
                    position={searchedPlace.position}
                    icon={{
                      url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                      scaledSize: new window.google.maps.Size(40, 40)
                    }}
                  />
                )}

                {selectedMarker && (
                  <InfoWindowF
                    position={{ lat: Number(selectedMarker.lat), lng: Number(selectedMarker.lng) }}
                    onCloseClick={() => {
                      setSelectedMarker(null);
                      setDirections(null);
                      setSelectedDestination(null);
                    }}
                  >
                    <div>
                      <h6>{selectedMarker.centerName}</h6>
                      <p>{selectedMarker.locality}</p>
                      {selectedMarker.billableSeats && (
                        <>
                          <p>Available Seats: {selectedMarker.billableSeats}</p>
                          <p>Price per Seat: {selectedMarker.pricePerSeat}</p>
                          <p>Available From: {selectedMarker.availableFrom}</p>
                        </>
                      )}
                      {directions && selectedDestination && selectedDestination.lat === selectedMarker.lat && (
                        <div style={{ borderTop: '1px solid #ccc', marginTop: '8px', paddingTop: '8px' }}>
                          <p><strong>Driving Distance:</strong> {directions.routes[0].legs[0].distance.text}</p>
                          <p><strong>Duration:</strong> {directions.routes[0].legs[0].duration.text}</p>
                        </div>
                      )}
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            </div>
          </div>

          <div className="col-lg-6 col-md-12">
            <div className="table-responsive">
              <div className="table-container">
                <table className="location-table table-bordered" style={{ maxHeight: "600px", overflowY: "scroll" }}>
                  <tbody>
                    {locations.map((item, index) => (
                      <tr
                        key={index}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                        onClick={(e) => handleTouchStart(e, index)}
                      >
                        <td>{index + 1}</td>
                        <td className='location-column p-3'>
                          {item.centerName || "N/A"}
                        </td>
                        <td className='p-3'>{item.address}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTab === 2 && (
        <Box
          sx={{
            mt: 2,
            mb: 3,
            position: 'relative'
          }}
        >
       
          <Table
            sx={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              '& th, & td': {
                border: '1px solid rgba(224, 224, 224, 1)',
                padding: '8px',
                verticalAlign: 'middle',
                textAlign: 'left'
              }
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: 'rgb(221, 221, 221)',
                  '& th': {
                    fontWeight: 'bold',
                    color: 'black',
                    textAlign: 'left'
                  }
                }}
              >
                <TableCell sx={{ width: isEditable ? '40%' : '50%' }}>Space Name</TableCell>
                <TableCell sx={{ width: isEditable ? '40%' : '50%' }}>Unviable Reason</TableCell>
                {isEditable && (
                  <TableCell sx={{ width: '20%', textAlign: 'center' }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {unviableOptions.map((option, index) => (
                <TableRow
                  key={option.id}
                  sx={{
                    '& td': {
                      border: '1px solid rgba(224, 224, 224, 1)',
                      padding: '8px',
                      verticalAlign: 'middle'
                    }
                  }}
                >
                  <TableCell sx={{ width: isEditable ? '40%' : '50%' }}>
                    {editingOptionId === option.id ? (
                      <TextField
                        fullWidth
                        variant="standard"
                        value={option.spaceName}
                        onChange={(e) => updateUnviableOptionAsync(option.id, 'spaceName', e.target.value)}
                        placeholder="Enter Space Name"
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            textAlign: 'left',
                            paddingLeft: 0
                          }
                        }}
                      />
                    ) : (
                      option.spaceName || 'N/A'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: isEditable ? '40%' : '50%' }}>
                    {editingOptionId === option.id ? (
                      <TextField
                        fullWidth
                        variant="standard"
                        value={option.unviableReason}
                        onChange={(e) => updateUnviableOptionAsync(option.id, 'unviableReason', e.target.value)}
                        placeholder="Enter Unviable Reason"
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            textAlign: 'left',
                            paddingLeft: 0
                          }
                        }}
                      />
                    ) : (
                      option.unviableReason || 'N/A'
                    )}
                  </TableCell>
                  {isEditable && (
                    <TableCell sx={{ width: '20%', textAlign: 'center' }}>
                      <IconButton
                        color="primary"
                        onClick={() => toggleEditMode(option.id)}
                        title={editingOptionId === option.id ? "Cancel Edit" : "Edit Option"}
                      >
                        {editingOptionId === option.id ? <CancelIcon /> : <EditIcon />}
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => removeUnviableOptionAsync(option.id)}
                        title="Remove Unviable Option"
                      >
                        <DeleteIcon />
                      </IconButton>
                      {(option.requiresSave || editingOptionId === option.id) && (
                        <IconButton
                          color="primary"
                          onClick={() => saveUnviableOptionsAsync()}
                          title="Save Unviable Option"
                        >
                          <SaveIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isEditable && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                mt: 2
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={addUnviableOptionAsync}
                startIcon={<AddCircleOutlineIcon />}
              >
                Add New Unviable Option
              </Button>
            </Box>
          )}
        </Box>
      )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  ) : (
    <div className="text-center">Loading...</div>
  );
};

export default Recommandedlocation;
